import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';
import { Sparkles, X, Send, Loader2, HelpCircle, PlayCircle, Video, Clock } from 'lucide-react';
import { MODULE_VIDEOS } from '../config/moduleVideos';

/**
 * Floating help assistant — bottom-right corner.
 * Streams answers from Gemini via SSE (token by token).
 */
const InAppHelpWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('videos'); // 'videos' | 'chat'
  const [playingVideo, setPlayingVideo] = useState(null); // MODULE_VIDEOS item
  const [messages, setMessages] = useState([]); // [{role:'user'|'assistant', text, streaming?:bool}]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);

  // Only show when authenticated
  const isAuth = !!localStorage.getItem('token');

  useEffect(() => {
    if (open && suggestions.length === 0 && isAuth) {
      api.get('/help/suggestions').then((r) => setSuggestions(r.data?.suggestions || [])).catch(() => {});
    }
  }, [open, suggestions.length, isAuth]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setBusy(true);
    setInput('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: question },
      { role: 'assistant', text: '', streaming: true },
    ]);

    try {
      const r = await api.post('/help/ask', {
        question,
        current_page: location.pathname,
      }, { timeout: 60000 });
      const answer = r.data?.answer || '';
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (!last || last.role !== 'assistant') return prev;
        return [...prev.slice(0, -1), { ...last, text: answer, streaming: false }];
      });
    } catch (e) {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (!last || last.role !== 'assistant') return prev;
        return [...prev.slice(0, -1), { ...last, text: `⚠️ Erreur : ${e.response?.data?.detail || e.message}`, streaming: false }];
      });
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask(input);
  };

  // Minimal markdown → JSX: **bold**, *italic*, line breaks, numbered/bullet lists
  const renderText = (raw) => {
    if (!raw) return null;
    return raw.split('\n').map((line, idx) => {
      const parts = [];
      let key = 0;
      let rest = line;
      const rx = /\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`/g;
      let lastIdx = 0;
      let m;
      while ((m = rx.exec(line)) !== null) {
        if (m.index > lastIdx) parts.push(<span key={key++}>{line.slice(lastIdx, m.index)}</span>);
        if (m[1]) parts.push(<strong key={key++} className="font-semibold text-slate-900">{m[1]}</strong>);
        else if (m[2]) parts.push(<em key={key++} className="text-orange-700">{m[2]}</em>);
        else if (m[3]) parts.push(<code key={key++} className="bg-slate-100 text-slate-800 px-1 rounded text-[0.85em]">{m[3]}</code>);
        lastIdx = m.index + m[0].length;
        rest = line.slice(lastIdx);
      }
      if (rest) parts.push(<span key={key++}>{rest}</span>);
      return (
        <React.Fragment key={idx}>
          {parts.length === 0 ? line : parts}
          <br />
        </React.Fragment>
      );
    });
  };

  if (!isAuth) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-orange-500 hover:bg-orange-600 text-white rounded-full w-14 h-14 shadow-2xl shadow-orange-500/30 flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Aide"
          data-testid="open-help-btn"
        >
          <HelpCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[min(440px,calc(100vw-2rem))] h-[min(640px,calc(100vh-3rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="help-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="font-semibold leading-tight">Aide Devis.Go</p>
                <p className="text-xs text-orange-100">Vidéos & assistant IA</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="close-help-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 bg-white shrink-0">
            <button
              type="button"
              onClick={() => setTab('videos')}
              className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border-b-2 transition ${
                tab === 'videos' ? 'text-orange-600 border-orange-500' : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
              data-testid="help-tab-videos"
            >
              <Video className="w-4 h-4" /> Vidéos
            </button>
            <button
              type="button"
              onClick={() => setTab('chat')}
              className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border-b-2 transition ${
                tab === 'chat' ? 'text-orange-600 border-orange-500' : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
              data-testid="help-tab-chat"
            >
              <Sparkles className="w-4 h-4" /> Assistant IA
            </button>
          </div>

          {tab === 'videos' && (
            <VideoList onPlay={setPlayingVideo} />
          )}

          {tab === 'chat' && (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {messages.length === 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600 leading-relaxed">
                      👋 Bonjour ! Je connais toutes les fonctionnalités de Devis.Go.
                      Tapez votre question, ou choisissez un exemple ci-dessous :
                    </p>
                    <div className="flex flex-col gap-2">
                      {suggestions.slice(0, 5).map((s) => (
                        <button
                          key={s}
                          onClick={() => ask(s)}
                          disabled={busy}
                          className="text-left text-xs bg-white border border-slate-200 hover:border-orange-300 hover:bg-orange-50 rounded-lg px-3 py-2 transition"
                          data-testid="help-suggestion"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-orange-500 text-white rounded-br-sm'
                          : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
                      }`}
                      data-testid={`help-message-${m.role}`}
                    >
                      {m.role === 'assistant' ? renderText(m.text) : m.text}
                      {m.streaming && (
                        <Loader2 className="inline w-3 h-3 animate-spin ml-1 text-orange-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              <form onSubmit={onSubmit} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Posez votre question…"
                  disabled={busy}
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-slate-50"
                  data-testid="help-input"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  className="bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-lg px-3 transition"
                  data-testid="help-send-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Video player modal */}
      {playingVideo && (
        <VideoPlayerModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
      )}
    </>
  );
};

// ============================================================================
// Video list — grouped by category
// ============================================================================
const VideoList = ({ onPlay }) => {
  const grouped = MODULE_VIDEOS.reduce((acc, v) => {
    (acc[v.category] = acc[v.category] || []).push(v);
    return acc;
  }, {});

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-5 bg-slate-50" data-testid="help-videos-list">
      <p className="text-xs text-slate-500 leading-relaxed">
        Cliquez sur une capsule pour découvrir en 2-3 minutes comment chaque module fonctionne.
      </p>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{cat}</p>
          <div className="space-y-2">
            {items.map((v) => {
              const available = !!v.youtubeId || !!v.vimeoId;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => available && onPlay(v)}
                  disabled={!available}
                  className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition ${
                    available
                      ? 'bg-white border-slate-200 hover:border-orange-300 hover:shadow-sm cursor-pointer'
                      : 'bg-slate-50 border-slate-200 cursor-not-allowed opacity-70'
                  }`}
                  data-testid={`help-video-${v.key}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    available ? 'bg-orange-100 text-orange-600' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <PlayCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{v.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{v.description}</p>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                      <span className="inline-flex items-center gap-1 text-slate-400">
                        <Clock className="w-3 h-3" /> {v.duration}
                      </span>
                      {!available && (
                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          Bientôt disponible
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Video player modal — YouTube or Vimeo embed
// ============================================================================
const VideoPlayerModal = ({ video, onClose }) => {
  const src = video.youtubeId
    ? `https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0`
    : `https://player.vimeo.com/video/${video.vimeoId}?autoplay=1`;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="help-video-modal"
    >
      <div
        className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-slate-100"
          data-testid="help-video-close"
          aria-label="Fermer"
        >
          <X className="w-5 h-5 text-slate-700" />
        </button>
        <iframe
          src={src}
          title={video.title}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

export default InAppHelpWidget;
